import React from "react";
import { Pill } from "@/components/nds/windows/resident/ui";

const COUNT_LABELS = [["notes", "Notes"], ["demerits", "NCNs"], ["demerit_points", "Points"], ["incidents", "Incidents"],
  ["incident_notes", "Incident notes"], ["pillar_answers", "Pillar answers"], ["homework_verified", "Homework verified"],
  ["homework_overdue", "Homework overdue"], ["restitution", "Restitution $"], ["memos", "Memos"], ["shifts_scheduled", "Shifts"],
  ["paychecks", "Paychecks"], ["gross_income", "Gross $"], ["savings_change", "Savings change $"], ["savings_balance", "Savings $"],
  ["checking_balance", "Cash in Hand $"], ["answers_scored", "Answers checked"], ["answers_follow_up", "Need follow-up"], ["check_ins_missed", "Missed check-ins"], ["appointments", "Appointments"], ["documents_added", "Documents"]];

export function ReviewSummary({ text, testid }) {
  return (
    <div data-testid={testid} className="space-y-2 text-sm text-nex-silver leading-relaxed">
      {text.replace(/\*\*/g, "").split(/\n+/).filter((p) => p.trim()).map((p, i) => {
        const m = p.match(/^(Overall read|Work & schedule|Money & savings|Conduct|Answer quality|Notes highlights|Watch items):\s*/i);
        if (m) return <p key={i} className="pt-1"><span className="font-mono text-[10px] tracking-[0.16em] uppercase text-nex-gold">{m[1]}</span> {p.slice(m[0].length)}</p>;
        const b = p.match(/^\s*-\s+/);
        return <p key={i} className={b ? "pl-4 -indent-2 text-[13px]" : ""}>{b ? `• ${p.slice(b[0].length)}` : p}</p>;
      })}
    </div>
  );
}

export function ReviewCounts({ counts }) {
  const items = COUNT_LABELS.filter(([k]) => counts?.[k]);
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map(([k, l]) => <Pill key={k} tone="dim">{l} · {counts[k]}</Pill>)}
    </div>
  );
}

const relTone = (r) => (r >= 70 ? "text-emerald-300 border-emerald-400/40" : r >= 40 ? "text-amber-300 border-amber-400/40" : "text-red-300 border-red-400/40");

export function AnswerScores({ scores, testid }) {
  if (!scores?.length) return null;
  const flagged = scores.filter((s) => s.follow_up).length;
  return (
    <div data-testid={testid} className="space-y-2">
      <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-nex-muted">
        Answer quality · {scores.length} checked · <span className={flagged ? "text-red-300" : ""}>{flagged} need follow-up</span> · staff only
      </div>
      <ul className="divide-y divide-white/5 border border-white/10 rounded-sm">
        {scores.map((s) => (
          <li key={s.id} data-testid={`${testid}-${s.id}`} className="px-3 py-2 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`px-1.5 py-0.5 border rounded-sm font-mono text-[11px] ${relTone(s.relevance)}`}>{s.relevance}%</span>
              <Pill tone="dim">{s.effort}</Pill>
              {s.follow_up ? <Pill tone="red">Follow up</Pill> : <Pill tone="green">No follow-up</Pill>}
              <span className="font-mono text-[10px] text-nex-muted">{s.kind} · {s.pillar_id?.replace("PILLAR_", "P")} · wk {s.week}</span>
            </div>
            <div className="text-xs text-nex-muted">Q: {s.question}</div>
            <div className="text-xs text-nex-silver">A: “{s.answer}”</div>
            {s.follow_up && s.why && <div className="text-xs text-red-200">Why: {s.why}</div>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ReviewCard({ r }) {
  return (
    <li data-testid={`review-${r.id}`} className="p-4 border border-white/10 rounded-sm space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-nex-muted">
        <Pill tone="gold">Last {r.days} days</Pill>
        <span>{new Date(r.reviewed_at).toLocaleString()}</span>
        <span>· {r.reviewed_by?.name || "—"}</span>
        {r.legacy_import && <Pill tone="dim">Old platform</Pill>}
      </div>
      <ReviewSummary text={r.summary} testid={`review-${r.id}-summary`} />
      <ReviewCounts counts={r.counts} />
      <AnswerScores scores={r.answer_scores} testid={`review-${r.id}-scores`} />
      {r.note && <p className="text-xs text-nex-chrome border-l-2 border-nex-gold/40 pl-2">Staff note: {r.note}</p>}
    </li>
  );
}
